import { useDebounce } from '@cast/utils';

import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import {
  PaginatedWorkloadCostSummariesQueryArgs,
  usePaginatedWorkloadCostSummariesQuery,
} from 'hooks/queries/cost/usePaginatedWorkloadCostSummariesQuery';
import { useParams } from 'hooks/useParams';

import { UseServerData } from '@cast/shared-search-ui';

export const useComputeCostWorkloads: UseServerData = ({
  values: _values,
  sortingState,
  enabled,
}) => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const { labels, namespaces, workloadNames, workloadTypes } =
    useDebounce(_values);
  const {
    adjustedDateRange,
    capabilitiesError,
    isCapabilitiesLoading,
    refetchCapabilities,
    useListingPrices,
    includeIdleCosts,
  } = useCostReportContext();

  const [from, to] = adjustedDateRange;
  const queryFilter: PaginatedWorkloadCostSummariesQueryArgs = {
    clusterId,
    startTime: from.toISOString(),
    endTime: to.toISOString(),
    labels,
    namespaces,
    workloadNames,
    workloadTypes,
    sortBy: sortingState?.columnId,
    sortDirection: sortingState?.sortBy.direction,
    enabled,
    useListingPrices,
    includeIdleCosts,
  };

  const res = usePaginatedWorkloadCostSummariesQuery(queryFilter);

  return {
    data: res.workloads,
    isLoading: res.isLoading || isCapabilitiesLoading,
    hasNextPage: !res.isLoading && res.hasNextPage,
    fetchNextPage: res.fetchNextPage,
    hasPreviousPage: !res.isLoading,
    error: res.error || capabilitiesError,
    refetch: () => {
      res.refetch();
      refetchCapabilities();
    },
    metadata: res.count || 0,
    isFetching: res.isFetching,
  };
};
